import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useJobsActions } from "tap/store/tapJobs/useJobs.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates job tasks data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchJobTasks function and job tasks state values
 */
export function useJobTasksData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateJobs } = useJobsActions();
  const {
    taskFilterForm,
    jobTasksRangeParam,
    jobTasksSortParam,
    jobTasksItemsPerPage,
  } = useSelector((s) => s.tapJobs);

  /**
   * Fetches job tasks from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,25")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux taskFilterForm
   * @param {Array|null} customSort - Optional sort array to use instead of Redux jobTasksSortParam
   * @param {boolean} silent - If true, don't show loading indicator (for auto-refresh)
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchJobTasks = useCallback(
    async (
      range,
      customFilterData = null,
      customSort = null,
      silent = false,
    ) => {
      const startTime = Date.now();
      if (!silent) {
        setLoadingTrue();
      }

      const filterData = customFilterData ?? taskFilterForm;
      const sortData = customSort ?? jobTasksSortParam;

      try {
        const data = await tapApi.jobTasks(
          filterCriteria(filterData, "", "jobTasks"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          if (!silent) {
            showErrorToast(data?.message || "Failed to fetch job tasks data.");
          }
          return null;
        }

        return data;
      } catch (error) {
        if (!silent) {
          showErrorToast(error?.message || "Failed to fetch job tasks data.");
        }
        console.error("Error fetching job tasks:", error);
        return null;
      } finally {
        if (!silent) {
          // Ensure minimum loading duration for consistent UX
          const elapsed = Date.now() - startTime;
          const remainingTime = MIN_LOADING_DURATION - elapsed;

          if (remainingTime > 0) {
            setTimeout(() => setLoadingFalse(), remainingTime);
          } else {
            setLoadingFalse();
          }
        }
      }
    },
    [
      taskFilterForm,
      jobTasksSortParam,
      setLoadingTrue,
      setLoadingFalse,
      showErrorToast,
    ],
  );

  return {
    fetchJobTasks,
    updateJobs,
    jobTasksRangeParam,
    jobTasksItemsPerPage,
    taskFilterForm,
    jobTasksSortParam,
  };
}
